import { Button, Divider, Select, Space, Spin } from "antd";
import type { SelectProps } from "antd/es/select";
import { getJobTitleResult } from "api/search.api";
import debounce from "lodash/debounce";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { gql, useLazyQuery } from "@apollo/client";
import { readToken } from "services/localStorage.service";
import { notificationController } from "../Notification/Notification";

const JOB_TITLE_VERIFIER = gql`
  query jobTitleVerifier($jobTitle: String!, $token: String!) {
    jobTitleVerifier(jobTitle: $jobTitle, token: $token) {
      valid
    }
  }
`;

export interface DebounceSelectProps<ValueType = any>
    extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
    fetchOptions: (search: string) => Promise<ValueType[]>;
    debounceTimeout?: number;
}

function DebounceSelect<
    ValueType extends {
        key?: string;
        label: React.ReactNode;
        value: string | number;
    } = any
>({
    fetchOptions,
    debounceTimeout = 800,
    setPositionData,
    size,
    ...props
}: DebounceSelectProps<ValueType> & { setPositionData: React.Dispatch<React.SetStateAction<string | null>> } & { size: string }) {
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<ValueType[]>([]);
    const [customOptions, setCustomOptions] = useState<ValueType[]>([]);
    const [error, setError] = useState<string | null>(null);
    const fetchRef = useRef(0);

    const [validateJobTitle, { data, loading, error: validationError }] =
        useLazyQuery(JOB_TITLE_VERIFIER);

    const debounceFetcher = useMemo(() => {
        const loadOptions = (value: string) => {
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setOptions([]);
            setFetching(true);
            setCustomOptions([]);
            setError(null);

            fetchOptions(value).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }
                const newCustomOptions = newOptions.filter(
                    (option: any) => option.isCustom
                );
                setCustomOptions(newCustomOptions);

                if (newOptions && newOptions.length)
                    setOptions(newOptions.filter((option: any) => !option.isCustom));
                setFetching(false);
            });
        };

        return debounce(loadOptions, debounceTimeout);
    }, [fetchOptions, debounceTimeout]);

    const handleCustomInput = async (inputValue: string) => {
        const dataD = await validateJobTitle({
            variables: { jobTitle: inputValue, token: readToken() },
        });

        if (dataD.loading) {
            setError("Validating...");
            return;
        }
        if (validationError) {
            setError("Validation error occurred.");
            return;
        }

        if (!dataD) {
            return;
        }
        // if (dataD && dataD.data && !dataD.data?.jobTitleVerifier?.valid) {
        //     setError("Invalid job title.");
        //     return;
        // }

        setError(null);
        setCustomOptions([]);
        saveToLocalStorage(inputValue);
        notificationController.success({
            message:
                "Position added successfully, please select the position from list",
        });

        const newOption = { label: inputValue, value: inputValue };

        // if (!options.some((opt) => opt.label === inputValue)) {
        //     setOptions((prevOptions: any) => [
        //         {
        //             label: inputValue,
        //             value: inputValue,
        //         },
        //         ...prevOptions,
        //     ]);
        // }
        if (!options.some((opt) => opt.label === inputValue)) {
            setOptions((prevOptions) => [{ label: inputValue, value: inputValue } as ValueType, ...prevOptions]);
        }
        console.log("newOption", newOption);
        console.log("inputValue", inputValue);
        setPositionData(inputValue);
    };

    const saveToLocalStorage = (jobTitle: string) => {
        const storedTitles = JSON.parse(localStorage.getItem("jobTitles") || "[]");
        if (!storedTitles.includes(jobTitle)) {
            storedTitles.push(jobTitle);
            localStorage.setItem("jobTitles", JSON.stringify(storedTitles));
        }
    };

    const loadFromLocalStorage = () => {
        const storedTitles = JSON.parse(localStorage.getItem("jobTitles") || "[]");
        return storedTitles.map((title: string) => ({
            label: title,
            value: title,
            isCustom: true,
        }));
    };

    return (
        <Select
            labelInValue
            filterOption={false}
            onSearch={debounceFetcher}
            size= {size}
            notFoundContent={
                fetching ? (
                    <Spin size="small" />
                ) : (
                    <>
                        {customOptions.length && customOptions[0].label ? (
                            <span
                                style={{ color: "#000" }}
                                className="ant-select-item ant-select-item-option ant-select-item-option-active"
                            >
                                Not found. Do you want to add your Custom Job title?
                            </span>
                        ) : (
                            <span style={{ color: "#000" }}>
                                Please start typing position to search{" "}
                            </span>
                        )}
                    </>
                )
            }
            options={[...options]}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            onInputKeyDown={(e: any) => {
                if (e.key === "Enter" && e.target.value) {
                    handleCustomInput(e.target.value);
                }
            }}
            dropdownRender={(menu) => {
                const isTitlePresent = options.some(
                    (option) => option.label === props.value
                );

                return (
                    <div>
                        {menu}
                        {customOptions.length &&
                            customOptions[0].label &&
                            !isTitlePresent ? (
                            <>
                                <Divider style={{ margin: "8px 0" }} />
                                <Space style={{ padding: "0 8px 4px" }}>
                                    {customOptions.map((option) => option.label)}
                                    <Button
                                        disabled={loading}
                                        type="link"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            handleCustomInput(customOptions[0].label as string);
                                            setPositionData(customOptions[0].label as string);
                                        }}
                                    >
                                        Add custom Job title
                                    </Button>
                                </Space>
                                {error && (
                                    <div style={{ color: "red", padding: "0 10px" }}>{error}</div>
                                )}
                            </>
                        ) : null}
                    </div>
                );
            }}
            {...props}
            allowClear
            // onChange={(newValue) => {
            //     if (!newValue) {
            //         setPositionData(null);
            //     } else if (Array.isArray(newValue)) {
            //         setPositionData(newValue.length > 0 ? String(newValue[newValue.length - 1].value) : null);
            //     } else {
            //         setPositionData(String((newValue as ValueType).value));
            //     }
            // }}
        />
    );
}

interface UserValue {
    label: string;
    value: string;
}

async function fetchUserList(username: string): Promise<UserValue[]> {
    return getJobTitleResult(username).then((data: any) => {
        let suggestions: UserValue[] = [];

        if (data || data?.data?.autocomplete || data.data.autocomplete.result) {
            const listData = data?.data?.autocomplete?.result ?? data;
            suggestions = listData.map((label: any) => ({
                label: label,
                value: label,
            }));
        }

        const exactMatch = suggestions.some(
            (suggestion) => suggestion.label === username
        );

        const storedTitles = JSON.parse(localStorage.getItem("jobTitles") || "[]");

        //Check if the username is already in the list of stored titles
        const filteredStoredTitles = storedTitles.filter((title: string) =>
            title.toLowerCase().includes(username.toLowerCase())
        );
        const storedFilteredTitles = filteredStoredTitles.map((title: string) => ({
            label: title,
            value: title,
        }));

        if (!exactMatch && !storedTitles.includes(username)) {
            suggestions.unshift({
                label: username,
                value: username,
                isCustom: true,
            } as any);
        }

        return [...storedFilteredTitles, ...suggestions];
    });
}

interface DropdownProps {
    className?: string;
    placeholder?: string;
    onClick?: any;
    onChange?: any;
    defaultValue?: any;
    valueSetterRequired?: any;
    mode?: "multiple" | "tags" | undefined;
    setPositionData: React.Dispatch<React.SetStateAction<string | null>>;
    positionData: string | null;
    size?: any;
    disabled?: boolean;
}

const MultiSearchableJobTitle: React.FC<DropdownProps> = ({
    className,
    placeholder,
    onClick,
    defaultValue,
    size ="large",
    valueSetterRequired,
    onChange,
    mode,
    setPositionData,
    positionData,
    disabled,
}) => {
    valueSetterRequired =
        valueSetterRequired == true || valueSetterRequired == false
            ? valueSetterRequired
            : true;

    useEffect(() => {
        console.log("positionData", positionData);
    }, [positionData]);

    return (
        <DebounceSelect
            mode={mode}
            showSearch
            value={defaultValue}
            placeholder={placeholder}
            fetchOptions={fetchUserList}
            onChange={(newValue) => {
                onChange && onChange(newValue);
            }}
            onClick={onClick}
            style={{ width: "100%" }}
            className={className}
            setPositionData={setPositionData}
            size={size}
            disabled={disabled}
        />
    );
};

export default MultiSearchableJobTitle;
